/**
 * LazyImage Component
 * Optimized image loading with Intersection Observer, skeleton placeholder,
 * and blur-up effect for better perceived performance.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { ImageIcon } from "lucide-react";

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  /** Show a blur placeholder while loading */
  blurPlaceholder?: boolean;
  /** Custom placeholder element */
  placeholder?: React.ReactNode;
  /** Callback when image loads */
  onLoad?: () => void;
  /** Callback when image fails to load */
  onError?: () => void;
  /** Root margin for Intersection Observer (default: "200px") */
  rootMargin?: string;
  /** Object fit style */
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
}

export function LazyImage({
  src,
  alt,
  className = "",
  width,
  height,
  blurPlaceholder = true,
  placeholder,
  onLoad,
  onError,
  rootMargin = "200px",
  objectFit = "cover",
}: LazyImageProps) {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin,
        threshold: 0.01,
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [rootMargin]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    onError?.();
  }, [onError]);

  // Determine aspect ratio for placeholder
  const aspectRatio = width && height ? `${width} / ${height}` : undefined;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{
        aspectRatio,
        minHeight: !aspectRatio ? "100px" : undefined,
      }}
    >
      {/* Skeleton placeholder */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse">
          {placeholder || (
            <div className="flex items-center justify-center h-full">
              <ImageIcon className="w-8 h-8 text-gray-300" />
            </div>
          )}
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-center p-4">
            <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No se pudo cargar la imagen</p>
          </div>
        </div>
      )}

      {/* Actual image */}
      {isInView && !hasError && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={`w-full h-full transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          } ${blurPlaceholder && !isLoaded ? "blur-sm scale-105" : ""}`}
          style={{
            objectFit,
          }}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
}

/**
 * LazyImage with responsive sizes
 */
interface ResponsiveLazyImageProps extends LazyImageProps {
  /** Array of source URLs for different sizes */
  srcSet?: Array<{ src: string; width: number }>;
  /** Sizes attribute for responsive images */
  sizes?: string;
}

export function ResponsiveLazyImage({
  srcSet,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  ...props
}: ResponsiveLazyImageProps) {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: props.rootMargin || "200px",
        threshold: 0.01,
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [props.rootMargin]);

  const srcSetString = srcSet?.map((s) => `${s.src} ${s.width}w`).join(", ");

  const aspectRatio =
    props.width && props.height
      ? `${props.width} / ${props.height}`
      : undefined;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${props.className || ""}`}
      style={{
        aspectRatio,
        minHeight: !aspectRatio ? "100px" : undefined,
      }}
    >
      {/* Skeleton placeholder */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse">
          {props.placeholder || (
            <div className="flex items-center justify-center h-full">
              <ImageIcon className="w-8 h-8 text-gray-300" />
            </div>
          )}
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-center p-4">
            <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No se pudo cargar la imagen</p>
          </div>
        </div>
      )}

      {/* Actual image with srcSet */}
      {isInView && !hasError && (
        <img
          src={props.src}
          srcSet={srcSetString}
          sizes={sizes}
          alt={props.alt}
          width={props.width}
          height={props.height}
          className={`w-full h-full transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          } ${props.blurPlaceholder && !isLoaded ? "blur-sm scale-105" : ""}`}
          style={{
            objectFit: props.objectFit || "cover",
          }}
          onLoad={() => {
            setIsLoaded(true);
            props.onLoad?.();
          }}
          onError={() => {
            setHasError(true);
            props.onError?.();
          }}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
}

/**
 * Preload critical images
 */
export function preloadImage(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Preload multiple images
 */
export function preloadImages(srcs: string[]): Promise<void[]> {
  return Promise.all(srcs.map(preloadImage));
}
